import React, { useMemo } from 'react'
import { Info, StatsCard } from '@demandlocal/components'
import { isEmpty, take } from 'lodash'
import { useParams } from 'react-router'
import { useGetDisplayConversionReports } from '../../../api/dealership/campaigns/reports/useGetDisplayConversionReports'
import { DateRange } from '../../../lib/types/DateRange'
import { formatCurrency, formatNumber } from '../../../lib/utils/formatNumber'
import PolarAreaChart from '../../Charts/PolarAreaChart'
import LineChart from '../../Charts/LineChart'
import { useGetDisplayCampaignById } from '../../../api/dealership/campaigns/useGetDisplayCampaignById'
import HorizontalBarChart from '../../Charts/HorizontalBarChart'

type Props = {
  filters: DateRange
}

const ConversionAttributionMetrics = ({ filters }: Props): JSX.Element => {
  const { campaign_id } = useParams()
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id })
  const {
    data: conversions,
    isPreviousData,
    isLoading,
  } = useGetDisplayConversionReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const roas = useMemo(() => {
    if (isEmpty(conversions?.roas)) return null
    return (
      Boolean(campaign?.show_roas) &&
      conversions?.roas &&
      Object.entries(
        conversions?.roas?.reduce((acc, { date, monetary_value }) => {
          acc[date] = acc[date] || { count: 0, monetaryValue: 0 }
          acc[date].count++
          acc[date].monetaryValue += monetary_value
          return acc
        }, {} as Record<string, { count: number; monetaryValue: number }>),
      ).map(([date, { count, monetaryValue }]) => ({ date, count, monetaryValue }))
    )
  }, [campaign?.show_roas, conversions?.roas])

  const hasKeyPages = useMemo(() => {
    return (
      (conversions?.values ?? [])
        .filter((d) => d.tag_id !== 4)
        .reduce((acc, cur) => acc + Number(cur.count), 0) > 0
    )
  }, [conversions?.values])

  return (
    <>
      {!isEmpty(conversions?.values) && (
        <div className="flex flex-col gap-8">
          <div className={'flex grid grid-cols-3 gap-5'}>
            <span className={'border-2 rounded-xl'}>
              <StatsCard
                title={'Total Page Views From Attributed Site Visits'}
                info={'Total page views from people who were exposed to an ad'}
                content={formatNumber(
                  conversions?.values
                    ?.filter((tag) => tag.tag_id === 4)
                    ?.map((data) => data.count)
                    .reduce((prev, acc) => {
                      return prev + acc
                    }, 0) ?? 0,
                )}
                color={'bg-white'}
              />
            </span>
            <span className={'border-2 rounded-xl'}>
              <StatsCard
                info={'Avg time between last impression and site visit'}
                title={'Average Time to Convert'}
                content={
                  Number(conversions?.time_to_convert) < 1
                    ? '<1 day'
                    : `${Math.floor(Number(conversions?.time_to_convert))} days`
                }
                color={'bg-white'}
              />
            </span>
            <span className={'border-2 rounded-xl'}>
              <StatsCard
                title={'Attributed Unique Site Visitors'}
                info={'Total number of unique site visitors who were exposed to an ad'}
                content={formatNumber(conversions?.unique_site_visitors ?? 0)}
                color={'bg-white'}
              />
            </span>
          </div>
          <div className={'flex flex-row flex-nowrap gap-5'}>
            {hasKeyPages && (
              <div className={'min-h-[270px] w-full border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Key Pages Viewed by Attributed Site Visits</span>
                  <Info
                    message={
                      'Site visits broken down by page from people who were exposed to an ad'
                    }
                  />
                </div>
                <div className={'h-[270px] p-2'}>
                  <PolarAreaChart
                    data={
                      conversions?.values
                        ?.filter((d) => d.tag_id !== 4)
                        ?.map((data) => {
                          return {
                            label: data.tag_name,
                            value: data.count,
                          }
                        })
                        .slice(0, 5) ?? []
                    }
                    isPreviousData={isPreviousData}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            )}
            <div className={'min-h-[270px] w-full border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Attributed Site Visits by UTM Source</span>
                <Info
                  message={
                    'Traffic sources credited for the attributed site visitors from users who saw our ad'
                  }
                />
              </div>
              <div className={'h-[270px] p-2'}>
                {hasKeyPages ? (
                  <PolarAreaChart
                    data={
                      take(
                        conversions?.utm_campaigns?.map((data) => {
                          return {
                            label: data.utm_campaign,
                            value: data.count,
                          }
                        }),
                        5,
                      ) ?? []
                    }
                    isPreviousData={isPreviousData}
                    isLoading={isLoading}
                  />
                ) : (
                  <HorizontalBarChart
                    data={
                      take(
                        conversions?.utm_campaigns?.map((data) => {
                          return {
                            label: data.utm_campaign,
                            value: data.count,
                          }
                        }),
                        5,
                      ) ?? []
                    }
                    isPreviousData={isPreviousData}
                    isLoading={isLoading}
                  />
                )}
              </div>
            </div>
          </div>
          {roas && (
            <div className={'flex grid grid-cols-2 gap-5'}>
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Total Orders</span>
                  <span>
                    {!isLoading &&
                      new Intl.NumberFormat('en-US').format(
                        roas
                          ?.map((metric: { count: number }) => metric.count)
                          .reduce((acc: number, cur: number) => acc + cur, 0),
                      )}
                  </span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <LineChart
                    data={roas?.map((d) => ({ date: d.date, count: d.count })) ?? []}
                    gradient={'blue-to-purple'}
                    yKey={'count'}
                    isLoading={isLoading}
                    isPreviousData={isPreviousData}
                  />
                </div>
              </div>
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Total Revenue</span>
                  <span>
                    {!isLoading &&
                      formatCurrency(
                        roas
                          ?.map(
                            (metric: { monetaryValue: number }) => metric.monetaryValue,
                          )
                          .reduce((acc: number, cur: number) => acc + cur, 0) || 0,
                      )}
                  </span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <LineChart
                    data={
                      roas?.map((d) => ({
                        date: d.date,
                        monetary_value: d.monetaryValue,
                      })) ?? []
                    }
                    isLoading={isLoading}
                    isPreviousData={isPreviousData}
                    gradient={'green-to-lemon-green'}
                    yKey={'monetary_value'}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default ConversionAttributionMetrics
